import * as Sentry from "@sentry/browser";
import axios, {
  AxiosError,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { addHours, isAfter, differenceInMinutes } from "date-fns";
import React from "react";

import DateFnsUtils from "@date-io/date-fns";

import _ from "lodash";

import { toast } from "@/design-system/components/Toast/Toast";

import {
  emptyWriteResponse,
  getPreviewHandler,
  isPreviewModeEnabled,
  isWriteKind,
  PREVIEW_PASSTHROUGH,
  wrapPreviewResponse,
} from "@/preview-mode";

import {
  CallRailIcon,
  CallTrackingMetricsIcon,
  ClearbitIcon,
  CustomerIOIcon,
  IntercomIcon,
  InvocaIcon,
  KustomerIcon,
  MailchimpIcon,
  MarchexIcon,
  SendGridIcon,
  SendInBlueIcon,
} from "./components/icons/themes/logos";
import {
  AndroidIcon,
  AndroidOsIcon,
  CodeIcon,
  ComputerMouseIcon,
  EditPropertyIcon,
  FingerprintIcon,
  HeightIcon,
  InternetConnectionIcon,
  LoadBalancerIcon,
  MultipleChoiceIcon,
  NaturalUserInterface2Icon,
  NetworkDriveIcon,
  TermIcon,
  ToggleOnIcon,
  TrashIcon,
} from "./components/icons/themes/windows10";
import { API_ROOT, inDev } from "./config";
import {
  AccountMetadata,
  envLocker,
  JSONValueStorage,
} from "./reducers/environments";
import {
  EventDefinitionFilter,
  LoadedDestinationInfo,
  LoadedEventDefinition,
} from "./types/global";
import useAccountMetadata from "./hooks/useAccountMetadata";
import { eraseCookie } from "./cookie";
import { AuthUser, getCurrentUser } from "./auth/firebase";
import { User } from "./components/users/types";

type Request = Record<string, any> | undefined;

let pendingPromise: AxiosPromise;

async function upsertAccountForUser(currentUser: AuthUser) {
  const accountCreated = new JSONValueStorage<boolean>(
    window.localStorage,
    `userAccountCreationConfirmed-${currentUser.uid}`,
  );
  if (!accountCreated.get() && pendingPromise) {
    try {
      await pendingPromise;
    } catch {}
  }
  return () => {
    if (!accountCreated.get()) {
      accountCreated.set(true);
    }
  };
}

type AdditionalAxiosRequestConfig = Omit<
  AxiosRequestConfig,
  "data" | "method" | "url"
>;

export const axiosInstance = axios.create({});
axiosInstance.interceptors.response.use(
  (response) => {
    // 2xx just pass through
    return response;
  },
  (error: AxiosError) => {
    // Non-2xx responses
    if (error.status && error.status >= 400) {
      Sentry.captureException(error);
    }
    return Promise.reject(error);
  },
);

// Make a request to the perfalytics API endpoint with the given request.
export function makeRequest<T>(
  kind: string,
  request: Request = {},
  additionalAxiosConfig: AdditionalAxiosRequestConfig = {},
): Promise<AxiosResponse<T>> {
  if (isPreviewModeEnabled()) {
    const handler = getPreviewHandler(kind);
    if (handler) {
      const result = handler(request);
      // A handler can decline this request and let it reach the real backend,
      // the same as an unregistered read kind. A declined request skips the
      // write block below, so a handler stays in full control of its own kind
      // even when that kind is a write.
      if (result !== PREVIEW_PASSTHROUGH) {
        return Promise.resolve(wrapPreviewResponse<T>(result as T));
      }
    } else if (isWriteKind(kind)) {
      console.warn(`[previewMode] blocked write: ${kind}`);
      // Surface the silenced write to the user. Without this they see the
      // form close cleanly but no list update — looks broken. The stable
      // id lets react-hot-toast collapse rapid repeats into a single visible
      // notification instead of stacking one per blocked write.
      toast.show(
        "Preview mode is on — your change wasn't saved. Disable preview mode to save for real.",
        { id: "preview-mode-blocked-write", icon: "🔒", duration: 4000 },
      );
      return Promise.resolve(
        wrapPreviewResponse<T>(emptyWriteResponse<T>(kind, request)),
      );
    }
    if (process.env.NODE_ENV === "development") {
      console.warn(`[previewMode] passthrough read: ${kind}`);
    }
  }

  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error("User undefined, cannot make requests");
  }

  window.sessionTimeoutManager?.verifySessionTimeoutOrLogOut(
    kind,
    masqueradeToken() !== null,
  );

  // TODO[IND-1682]: what is upsertAccountForUser?
  const response = upsertAccountForUser(currentUser).then((cb) => {
    const out = makeApiRequestNoConcurrencyCheck<T>(
      currentUser,
      kind,
      request,
      additionalAxiosConfig,
    );
    out.then(cb);
    return out;
  });
  if (!pendingPromise) {
    pendingPromise = response;
  }
  return response;
}

async function makeApiRequestNoConcurrencyCheck<T>(
  currentUser: AuthUser,
  kind: string,
  request: Request = {},
  additionalAxiosConfig: AdditionalAxiosRequestConfig = {},
): Promise<AxiosResponse<T>> {
  const token = await currentUser.getIdToken();
  const query: Record<string, any> = {
    kind: kind,
    token: token,
    request: request,
    envID: envLocker.getEnvID(),
    email: currentUser.email,
  };

  const anyWindow = window as any;
  if (
    anyWindow.freshpaint &&
    typeof anyWindow.freshpaint.track === "function"
  ) {
    anyWindow.freshpaint.track(kind, { envID: envLocker.getEnvID() });
  }

  if (masqueradeToken()) {
    query.masquerade_token = masqueradeToken();
  }

  const sim = simulatedAccess();
  if (sim) {
    query.simulate_permissions = sim.permissions;
  }

  const response = await axiosInstance.request<T>({
    method: "post",
    url: `${API_ROOT}/list?t=${kind}`,
    data: query,
    ...additionalAxiosConfig,
  });

  return response;
}

const AUTH_TOKEN_COOKIE_KEY = "session";

export function clearAuthCookies(): void {
  eraseCookie(AUTH_TOKEN_COOKIE_KEY);
}

export const MASQUERADE_LOCAL_STORAGE_KEY = "masquerade_token";

// Causes the current user to begin masquarading under the given
// environment or user. Masquarading lasts for two hours.
export function masqueradeAs(token: string) {
  const data = {
    expires: addHours(new Date(), 2).getTime(),
    token: token,
  };

  localStorage.setItem(MASQUERADE_LOCAL_STORAGE_KEY, JSON.stringify(data));
}

// Gets the current token the user is masquerading under.
export function masqueradeToken(): string | null {
  const data = localStorage.getItem(MASQUERADE_LOCAL_STORAGE_KEY);
  if (data === null) {
    return null;
  }

  const { expires, token } = JSON.parse(data);
  if (expires === undefined || token === undefined) {
    return null;
  }

  if (isAfter(new Date(), new Date(expires))) {
    stopMasquerading();
    return null;
  }

  return token;
}

// Causes the current user to stop masquerading.
export function stopMasquerading() {
  localStorage.removeItem(MASQUERADE_LOCAL_STORAGE_KEY);
}

// Returns the number of minutes the user has left masquerading.
export function masqueradingMinutesLeft(): number | null {
  const data = localStorage.getItem(MASQUERADE_LOCAL_STORAGE_KEY);
  if (data === null) {
    return null;
  }

  const { expires, token } = JSON.parse(data);
  if (expires === undefined || token === undefined) {
    return null;
  }

  const now = new Date();
  const expiresTime = new Date(expires);
  if (isAfter(now, expiresTime)) {
    stopMasquerading();
    return null;
  }

  return Math.ceil(differenceInMinutes(expiresTime, now));
}

export const SIMULATE_PERMISSIONS_LOCAL_STORAGE_KEY = "simulate_permissions";

// SimulatedAccess is the internal permission-simulation tool's persisted state:
// the restricted permission set to evaluate the superuser against. When
// backendOnly is set, the set is still sent to the backend (which always
// enforces it) but the frontend gates ignore it, so the UI stays unrestricted
// and the user can confirm the backend blocks unauthorized actions on its own.
export type SimulatedAccess = {
  permissions: Record<string, string[]>;
  backendOnly: boolean;
};

// Begins simulating a restricted access set for the current (superuser) user.
// Persists like the masquerade token (localStorage, 2h TTL).
export function setSimulatedAccess(access: SimulatedAccess) {
  const data = {
    expires: addHours(new Date(), 2).getTime(),
    permissions: access.permissions,
    backendOnly: access.backendOnly,
  };

  localStorage.setItem(
    SIMULATE_PERMISSIONS_LOCAL_STORAGE_KEY,
    JSON.stringify(data),
  );
}

// Returns the active simulated access set, or null when none is set or it has
// expired (expiry self-clears, mirroring masqueradeToken). This is the
// transport-facing accessor: the set is always sent to the backend. Frontend
// gates must use frontendSimulatedAccess, which honours backendOnly.
export function simulatedAccess(): SimulatedAccess | null {
  const data = localStorage.getItem(SIMULATE_PERMISSIONS_LOCAL_STORAGE_KEY);
  if (data === null) {
    return null;
  }

  const { expires, permissions, backendOnly } = JSON.parse(data);
  if (expires === undefined) {
    return null;
  }

  if (isAfter(new Date(), new Date(expires))) {
    clearSimulatedAccess();
    return null;
  }

  return {
    permissions: permissions ?? {},
    backendOnly: backendOnly ?? false,
  };
}

// Returns the simulation the frontend RBAC gates should enforce: the active set
// unless it is backend-only, in which case the gates behave as if no simulation
// is set (the UI stays unrestricted; only the backend enforces).
export function frontendSimulatedAccess(): SimulatedAccess | null {
  const sim = simulatedAccess();
  if (sim === null || sim.backendOnly) {
    return null;
  }
  return sim;
}

export function clearSimulatedAccess() {
  localStorage.removeItem(SIMULATE_PERMISSIONS_LOCAL_STORAGE_KEY);
}

// Returns the number of minutes left on the active simulation, or null if none.
export function simulatingMinutesLeft(): number | null {
  const data = localStorage.getItem(SIMULATE_PERMISSIONS_LOCAL_STORAGE_KEY);
  if (data === null) {
    return null;
  }

  const { expires } = JSON.parse(data);
  if (expires === undefined) {
    return null;
  }

  const now = new Date();
  const expiresTime = new Date(expires);
  if (isAfter(now, expiresTime)) {
    clearSimulatedAccess();
    return null;
  }

  return Math.ceil(differenceInMinutes(expiresTime, now));
}

// Determines if the masquarade bar should be shown or not, based on whether or
// not the user is masquarading and whether environment metadata has the bar
// turned off (used for some demo accounts).
export function useMasqueradeBarOpen() {
  const accountMetadata = useAccountMetadata();
  const whoAmI = masqueradeToken();

  return (
    whoAmI !== null && !accountMetadata?.config?.disableMasqueradeWarningBanner
  );
}

// Returns true when the app is running in a local development build (vite dev
// server, ENV=dev). Vite statically replaces process.env.NODE_ENV with
// "production" in deployed builds, so anything gated on this helper is
// compiled out of production bundles entirely.
export function isLocalDevBuild(): boolean {
  return process.env.NODE_ENV === "development";
}

// Returns true if this user is allowed to toggle restricted feature flags, for
// example HIPAA Mode
// This list should be limitted to engineers, support engineers, and customer
// success engineers
export function userCanToggleRestrictedFeatures(): boolean {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    return false;
  }

  const restrictedAccessUIDs = [
    "c7OMfJqaJvhWehai8cANbiLy4CH3", // James Silvester
    "zSsBJuUHpleGzxFs4NC13bfcHMa2", // Joey Bloom
    "eQJnCkI63aMqnIfjdTz9ybPhitt2", // Gil Cayabyab
    "y1PIL0F1yqXVD4rIOIV5m2GZusn1", // Gina Im
    "ZB6BDjW6NKY03brwtjzslZV5zz42", // Buc Rogers
    "jH3vVDXOkxZt3NAPeth5RAUwCKi1", // Andrew Lai
    "GkDMY4SvXQfPRZfO0cRQAJ6Ygzb2", // Hillary Zinner
    "YJNDAIoXe9dc4KTgdelWteFWxFP2", // Dylan Nugent
    "qMYI7qxCRFgqUregeq1GMZts19n1", // Evan Casey
    "llvqyNxcdDdZZbxypDU8XjBXd4m1", // Cece Fowler
    "CWIDT73mGMSjThjyuptvaTx0qal1", // Jamie Lee
    "ZK8Ep6COZecd7xnXpasPK7FBUqE2", // Mara Pritchard
    "TfWudR9d37ROeqzWGsNCaQbpXO33", // Elyssa Spector
    "uBJJFJoZNKQ9xEcmQWdeoAtiKaW2", // Lane Zimmerman
    "Lb95HzHRI6UOUioTQSCHQ15qqmN2", // Paul Bersch
    "Dm1fY6QkOSYaR4so2mpYfX9vwtX2", // Zak Holt
    "j7buND5xedY8RnXMgjBpMyr95kI3", // Kara Ebrahim
    "UcmXujZYl1VfWQnW5EJRSrQyYUc2", // Dan Godwin
    "uD1HDoyc8ueDalmSayuA2e70Fcj2", // Pavan Gollapalli
    "KhMjXlxx60NtqWHQ2oELbHgJVQq1", // Austin Curtis
    "YRZHmYMdFNOciKLCIA6ue0cHap93", // Hannah Wolff
    "HcClp8HMDPUIrO3mhh3adp0ReMA3", // Seth Delorme
    "1hntTeN0EqhW4QaVJ9zuJH1Ttol2", // Isaac Householder
    "a7oI9bmhtsXksfSjglNOJK7WIwi1", // Cody Smith
    "ud8AlgljJeg5QA8H03iWImUw7tg2", // Dave Cameron
    "UVk7CM0WbJY4B7pSfwjBM9So0n32", // Kyle Wright
    "PxNgzLL4BAShSvwESNPCNApMv1i2", // Alan Chen
    "z7Ta568OSagS9BQb60IBbXITpKm2", // Kelly Tengco
    "FkSDbJ9dOdc1AxEdpuuv17AlSBO2", // Mark Rogers
    "r19fNcPxlqh2PKB4IDOzv5aBHyj1", // Rachael Charewicz
    "pb6hvtWd7hcyVl59Vd1PbeO7k7T2", // Tom Spiegelman
    "r4ALJheH0CbqrgiUxWisvt58oIG2", // Daniela Bloch
    "Kq7J3lBrGvXgkc0NhjZnIUvw6l33", // Audrey Atherton
    "A8xmnXZoVjM4V1oKZSmmqEulbSn2", // Ian Spence
    "IQgJHsgAiAPxMIlbYHz9buqGheb2", // Rocco Farrell
    "D5sQLKbv92PEDGd8eEMFQxYnDdH2", // Dylan Scott
    "HTe81IFtmhfws4VOIm46L0yyH1M2", // Rick Howard
  ];

  return restrictedAccessUIDs.includes(currentUser.uid);
}

// Returns true if this user is allowed to masquerade.
export function userIsFPEmployee(): boolean {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    return false;
  }

  return specifiedUserIsFPEmployee(currentUser.uid);
}

// Prod: employee + masquerade only (sales-demo surface). Localhost skips the
// masquerade requirement so the overlay can be click-tested on a local env.
export function canViewPrivacyDashboard(): boolean {
  if (!userIsFPEmployee()) {
    return false;
  }
  return masqueradeToken() !== null || inDev;
}

// Returns true if this user is allowed to masquerade.
export function specifiedUserIsFPEmployee(uid: string): boolean {
  const freshpaintUIDs = [
    "43gsfPYPROVpNCR6Kp66gMo27kP2", // John Mullarkey
    "uHrRWjoyEJaKEaUFxvAREPwVbxg1", // Alissa Winberry
    "s08dRqISB1Z1mOU2CDAktS6H2MG3", // Alexander Castiglione
    "c7OMfJqaJvhWehai8cANbiLy4CH3", // James Silvester
    "6qpIdfaoAOWsGKCv7ciq4khZb6t1", // Ray Mina
    "zSsBJuUHpleGzxFs4NC13bfcHMa2", // Joey Bloom
    "eQJnCkI63aMqnIfjdTz9ybPhitt2", // Gil Cayabyab
    "y1PIL0F1yqXVD4rIOIV5m2GZusn1", // Gina Im
    "ZB6BDjW6NKY03brwtjzslZV5zz42", // Buc Rogers
    "BRzULAbdemSHQyddmkWKq5J4qgj1", // Dave Twichell
    "MK0r4k8CHtPxrWI1McA2z0NCsBm2", // Bianchi
    "cYORm6AkkuTkBRP6yGYRR48Azco1", // Dayna Malick
    "jH3vVDXOkxZt3NAPeth5RAUwCKi1", // Andrew Lai
    "GkDMY4SvXQfPRZfO0cRQAJ6Ygzb2", // Hillary Zinner
    "1bbsID1yJZVEXZFBMtHcNQUo6EM2", // Varun Penamatsa
    "eW40m8EWYVgkIRc9hhFrlbeuRfk1", // Tim Rourke
    "1hntTeN0EqhW4QaVJ9zuJH1Ttol2", // Isaac Householder
    "YJNDAIoXe9dc4KTgdelWteFWxFP2", // Dylan Nugent
    "qMYI7qxCRFgqUregeq1GMZts19n1", // Evan Casey
    "g6zzxH9mEbf68R8SHBTXGkAN8d03", // Ellen Barrett
    "llvqyNxcdDdZZbxypDU8XjBXd4m1", // Cece Fowler
    "B1MZCTqty7NvaATMKdQDtEQZHTH3", // Rachael White
    "a7oI9bmhtsXksfSjglNOJK7WIwi1", // Cody Smith
    "epfjM7lXrrbv1ojCmQ3lyK4jY4C2", // Sam McCall
    "OcBak8fRc7ZKVeMTAW0pvB7SYDG3", // Kristin Sykes
    "CWIDT73mGMSjThjyuptvaTx0qal1", // Jamie Lee
    "IJ6ZXEQg6nOIWmYWYTFxvg02EXh2", // Jess Sforza
    "ZK8Ep6COZecd7xnXpasPK7FBUqE2", // Mara Pritchard
    "UnnvLWcwMjhhMZCx8rw4I8LM8je2", // Kylie Storts
    "TfWudR9d37ROeqzWGsNCaQbpXO33", // Elyssa Spector
    "uBJJFJoZNKQ9xEcmQWdeoAtiKaW2", // Lane Zimmerman
    "Lb95HzHRI6UOUioTQSCHQ15qqmN2", // Paul Bersch
    "CSzuFDXzQXYVNvIgDGm9BTeBnQR2", // Joshua Jacobson
    "qz2iaZkbPCeN4HcOuo6NuruA8v02", // JT Ibe
    "SUdillaEOlSPYwC3J6ydtuUuY1N2", // David Chai
    "Posi2ThK3ATNAr773tlX8HkfJA72", // Derek Sargent
    "plyWYKzJanRchVlZtVpwaezD3Ql1", // Steve Brady
    "NahZn1dPLCNySeX6OJF8E6ShjTx2", // Faith Allen
    "TApHasjGo0PuMR1vFWahkKbafP02", // Brianna Padilla
    "WHWpxVNOUuNr8DpNKlRlhecV5xh1", // Chelsea Dougherty
    "Dm1fY6QkOSYaR4so2mpYfX9vwtX2", // Zak Holt
    "j7buND5xedY8RnXMgjBpMyr95kI3", // Kara Ebrahim
    "PAEQQXbI4ffVjbgDiDi4ncIz2IG3", // Carlos Crawford
    "udFALlO6nBRsndvIArqFcLkxhpJ2", // Shezal Padani
    "gSTG9WuAahXkR7cMjm2ChRYxmBg1", // Orinna Barton
    "JC02jeoCnxVtYdTCaN32Z9ahhjO2", // Lauren Yeates
    "j3JS38BmjPdWROljtN54KSe5M4f1", // Chris Chan
    "UcmXujZYl1VfWQnW5EJRSrQyYUc2", // Dan Godwin
    "uD1HDoyc8ueDalmSayuA2e70Fcj2", // Pavan Gollapalli
    "KhMjXlxx60NtqWHQ2oELbHgJVQq1", // Austin Curtis
    "YRZHmYMdFNOciKLCIA6ue0cHap93", // Hannah Wolff
    "HcClp8HMDPUIrO3mhh3adp0ReMA3", // Seth Delorme
    "E5Wg3HFYKsMZngQENqsGx6L26O33", // Emily Chien
    "ud8AlgljJeg5QA8H03iWImUw7tg2", // Dave Cameron
    "UVk7CM0WbJY4B7pSfwjBM9So0n32", // Kyle Wright
    "PxNgzLL4BAShSvwESNPCNApMv1i2", // Alan Chen
    "KjaTvb0sr1RslESoCRoHosgcqAw1", // Becca Wilson
    "GoDUsj4QQcY911KbKcsvy08AsiF2", // Christian D'Alessandro
    "z7Ta568OSagS9BQb60IBbXITpKm2", // Kelly Tengco
    "pV5bZnXddRb2V9LxnAiZiHaakFS2", // Julia Wilcox
    "NOq0rAcgFJPjlsBm4WcD4pxwYsx2", // Erik Brice
    "XCZqx71vW6RMq4JTl0blFwPAt1x1", // Keiji Parkhurst
    "2FVhCgP5TdgRsvTEsHSzt89elUE3", // Luly Diez
    "DB5usaC2YbVwCAeYIl7Fpo2KbPo1", // Daniela Anadon
    "rcyhuLaSW6aarswIoPUlrW06gG13", // Nick Hinzmann
    "n4xfqlJN3tYpgFcJJf00dz3Zqvg1", // Bowie Tomlinson
    "n1NAJwpKcNY29SRPBK8PPmR9mqz2", // Ryan Decker
    "gKXJAzSd4CUnYBT3LyFSok9McmY2", // Samantha Reuter
    "olJbeJe1aiN6TKxlxWBb1sX5QaC3", // Hannah Breunig
    "FkSDbJ9dOdc1AxEdpuuv17AlSBO2", // Mark Rogers
    "r19fNcPxlqh2PKB4IDOzv5aBHyj1", // Rachael Charewicz
    "D5sQLKbv92PEDGd8eEMFQxYnDdH2", // Dylan Scott
    "3R07WfE4wqWS39vL2Qf1mWQrP5w2", // Neta Iser
    "pb6hvtWd7hcyVl59Vd1PbeO7k7T2", // Tom Spiegelman
    "r4ALJheH0CbqrgiUxWisvt58oIG2", // Daniela Bloch
    "Kq7J3lBrGvXgkc0NhjZnIUvw6l33", // Audrey Atherton
    "L5PLFU3cRahwRe58tM5jN7fouZz2", // Xan Spewak
    "38b6xTnAYMh3RN6khs3nw6W6v4o1", // John Whaley
    "3MGybN3yuaNWmRQWvX099WGO9C83", // Avisha Sloat
    "vwzA1AXzHOMYFXTrpyPui0vVio43", // Jesse Geers
    "4NFoDAoLFFRs1MvYE9B5Q6BqqG83", // Anya Phillips
    "B59ygGrhukW4c8XiLDsGulqszxy2", // John Raposo
    "yTYnGWVtUtY613sDR13qhXCXpFX2", // Alex Margolis
    "DCxMs7msjmVaJb4lUYFRCYhNNJt1", // Chris Kornbrust
    "A8xmnXZoVjM4V1oKZSmmqEulbSn2", // Ian Spence
    "yV7oT8c4uuTKazd6shTGp1KUZGr1", // Melissa Morse
    "tY8MDzuCvmQkEnbAwaYREyyLU7u1", // Silvia Macareno
    "YtIDqXvelpM0Srbv1yFCEBcOKzx1", // Alana Matos
    "IQgJHsgAiAPxMIlbYHz9buqGheb2", // Rocco Farrell
    "poXfBtOOaMRp7u1xj76xp3DT59t1", // Jeric Griffin
    "LU3NQj437bSRtNIHm6NsWcuEahp1", // Elizabeth Bradley
    "OVRGxqHdGdSpjSWwYxhmJMNd1hg1", // Jordan Kohun
    "rCmU6Dbeu6QgPk03Nb6mOIt158u2", // Patrick Smith
    "HTe81IFtmhfws4VOIm46L0yyH1M2", // Rick Howard
    "LBwZeKe5XueXwYOrS4kqRi9DLzB3", // Lizzie Chapman
  ];

  return freshpaintUIDs.includes(uid);
}

// Account-admin access, for decisions recorded against the account (accepting the
// SR/HM privacy notice) as well as plain admin UI. Unlike userIsCROAdmin below it
// does not honour the Freshpaint-employee roster: an employee signed in as
// themselves is not an admin of someone else's account, and a record they produced
// would be indistinguishable from a customer admin's.
//
// A masquerading session does pass, deliberately, so support can act when a
// customer is stuck. Anything durable written behind this predicate is expected to
// record the masquerade state (see is_masquerading on
// consent_privacy_nudge_acknowledgments) so the two are never conflated.
export function userHasAccountAdminAccess(user: User | undefined): boolean {
  // A simulated set is always a restricted, non-admin principal.
  if (frontendSimulatedAccess()) return false;
  if (masqueradeToken() != null) return true;
  return user?.role === "admin";
}

// Canonical admin predicate for CRO/AI features. FP employees and
// masquerading sessions bypass the role check so support can debug
// customer-facing flows without an account-admin login.
export function userIsCROAdmin(user: User | undefined): boolean {
  // A simulated set is always a restricted, non-admin principal.
  if (frontendSimulatedAccess()) return false;
  if (masqueradeToken() != null) return true;
  if (userIsFPEmployee()) return true;
  return user?.role === "admin";
}

export const iconForEventType = (
  eventType: string,
  library?: string,
): React.ReactNode => {
  const sourceIcons: Array<[string, React.ForwardRefExoticComponent<any>]> = [
    ["sendinblue", SendInBlueIcon],
    ["mailchimp", MailchimpIcon],
    ["intercom", IntercomIcon],
    ["sendgrid", SendGridIcon],
    ["customer-io", CustomerIOIcon],
    ["kustomer", KustomerIcon],
    ["call-rail", CallRailIcon],
    ["ctm", CallTrackingMetricsIcon],
    ["marchex", MarchexIcon],
    ["invoca", InvocaIcon],
    ["clearbit", ClearbitIcon],
  ];

  if (library) {
    for (let source of sourceIcons) {
      const [sourceName, icon] = source;
      if (library.toLowerCase().includes(sourceName)) {
        return icon;
      }
    }
  }

  switch (eventType) {
    case "pageview":
      return InternetConnectionIcon;
    case "network":
      return NetworkDriveIcon;
    case "click":
      return ComputerMouseIcon;
    case "submit":
      return TermIcon;
    case "change":
      return EditPropertyIcon;
    case "$identify":
      return FingerprintIcon;
    case "manual_tracked":
      return CodeIcon;
    case "server":
      return LoadBalancerIcon;
    case "analytics-android":
      return AndroidOsIcon;
    case "analytics-ios":
      return AndroidOsIcon;
    case "touch":
      return NaturalUserInterface2Icon;
    case "text_edit":
      return EditPropertyIcon;
    case "toggle":
      return ToggleOnIcon;
    case "react_navigation_screenview":
      return AndroidIcon;
    case "scroll_view_page":
      return HeightIcon;
    case "rn_custom":
      return CodeIcon;
    case "hidden":
      return TrashIcon;
    case "$survey":
      return MultipleChoiceIcon;
    default:
      return CodeIcon;
  }
};

export const iconForEventSource = (eventSource: string): React.FC<any> => {
  switch (eventSource) {
    case "autotrack":
      return CodeIcon;
    default:
      return () => null;
  }
};

export const eventSourceDisplay = (eventSource: string): string => {
  switch (eventSource) {
    case "autotrack":
      return "Web";
    default:
      return "Custom";
  }
};

export const isValidUrl = (inputUrl: string): boolean => {
  try {
    new URL(inputUrl);
  } catch {
    return false;
  }
  return true;
};

const scrubbedOutboundHostSuffixes = [
  ".local",
  ".internal",
  ".test",
  ".localhost",
];

const isUnsafeOutboundIPv4 = (host: string): boolean => {
  const match = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host);
  if (!match) {
    return false;
  }

  const octets = match.slice(1).map(Number);
  if (octets.some((octet) => octet > 255)) {
    return true;
  }

  const [a, b] = octets;
  if (a === 0) {
    return true;
  }
  if (a === 127) {
    return true;
  }
  if (a === 10) {
    return true;
  }
  if (a === 172 && b >= 16 && b <= 31) {
    return true;
  }
  if (a === 192 && b === 168) {
    return true;
  }
  if (a === 169 && b === 254) {
    return true;
  }
  if (a >= 224) {
    return true;
  }

  return false;
};

const isUnsafeOutboundIPv6 = (host: string): boolean => {
  const normalized = host.toLowerCase();
  if (normalized === "::1" || normalized === "::") {
    return true;
  }
  if (normalized.startsWith("fe80:")) {
    return true;
  }
  if (normalized.startsWith("fc") || normalized.startsWith("fd")) {
    return true;
  }
  if (normalized.startsWith("ff")) {
    return true;
  }

  return false;
};

const isUnsafeOutboundHost = (host: string): boolean => {
  const normalized = host.toLowerCase().replace(/^\[|\]$/g, "");
  if (!normalized) {
    return true;
  }

  if (isUnsafeOutboundIPv4(normalized) || isUnsafeOutboundIPv6(normalized)) {
    return true;
  }

  if (!normalized.includes(".")) {
    return true;
  }

  return scrubbedOutboundHostSuffixes.some((suffix) =>
    normalized.endsWith(suffix),
  );
};

export const validateTrackingDomainUrl = (
  value: unknown,
): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  if (trimmed.startsWith("http://")) {
    return "Tracking domain must use HTTPS (e.g. https://tracking.example.com)";
  }

  if (!trimmed.startsWith("https://")) {
    return "Enter a valid URL starting with https:// (e.g. https://tracking.example.com)";
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return "Enter a valid URL (e.g. https://tracking.example.com)";
  }

  if (parsed.protocol !== "https:") {
    return "Tracking domain must use HTTPS (e.g. https://tracking.example.com)";
  }

  if (isUnsafeOutboundHost(parsed.hostname)) {
    return "Enter a public HTTPS tracking domain (e.g. https://tracking.example.com)";
  }

  return undefined;
};

const PAGE_SIZE = 1000;

// Wrapper function which implements pagination for getting all the event definitions.
// This function is duplicated in the editor directory.
export const getEventDefs = async (): Promise<{
  event_defs: LoadedEventDefinition[];
  dest_info: LoadedDestinationInfo[];
}> => {
  var prevResponse = await makeRequest<{
    event_defs: LoadedEventDefinition[];
    dest_info: LoadedDestinationInfo[];
  }>("get_event_defs", { page_size: PAGE_SIZE });

  var allEventDefs: LoadedEventDefinition[] = [];
  allEventDefs = allEventDefs.concat(prevResponse.data.event_defs);

  while (prevResponse.data.event_defs.length === PAGE_SIZE) {
    prevResponse = await makeRequest<{
      event_defs: LoadedEventDefinition[];
      dest_info: LoadedDestinationInfo[];
    }>("get_event_defs", {
      page_size: PAGE_SIZE,
      start_after_id: allEventDefs[allEventDefs.length - 1].id,
      start_after_name: allEventDefs[allEventDefs.length - 1].name,
    });
    allEventDefs = allEventDefs.concat(prevResponse.data.event_defs);
  }

  return {
    event_defs: allEventDefs,
    dest_info: prevResponse.data.dest_info,
  };
};

export const isDestinationInHIPAAMode = (
  dest: LoadedDestinationInfo,
): boolean => {
  return dest.connection_mode === "server" && !dest.has_baa;
};

const beautifulPropNames: Record<string, string> = {
  mp_lib: "Event Source",
  gclid: "Google Click ID (GCLID)",
  dclid: "DoubleClick Click ID (DCLID)",
  gclsrc: "Google Click ID Source (GCLSRC)",
  twclid: "Twitter Click ID (TWCLID)",
  msclkid: "Bing Click ID (MSCLKID)",
  fbclid: "Facebook Click ID (FBCLID)",
  sapid: "StackAdapt Postback ID",
  ttdimp: "TheTradeDesk Impression ID",
  irclickid: "Impact.com Click ID",
  li_fat_id: "LinkedIn Ads Click ID (LI_FAT_ID)",
  rdt_cid: "Reddit Ads Click ID (rdt_cid)",
  epik: "Pinterest Click ID (EPIK)",
  survey_event_type: "Survey Event Type",
  survey_id: "Survey ID",
  survey_version: "Survey Version",
  survey_question_index: "Survey Question Index",
  survey_response_value: "Survey Response",
};

export const beautifyBuiltinPropName = (name: string): string => {
  if (beautifulPropNames[name]) {
    return beautifulPropNames[name];
  }

  name = _.startCase(name);

  name = name.replace(/(\b)id(\b)/gi, "$1ID$2");
  name = name.replace(/(\b)ids(\b)/gi, "$1IDs$2");
  name = name.replace(/(\b)ip(\b)/gi, "$1IP$2");
  name = name.replace(/(\b)url(\b)/gi, "$1URL$2");
  name = name.replace(/(\b)UTM(\b)/gi, "$1UTM$2");
  name = name.replace(/(\b)CSS(\b)/gi, "$1CSS$2");
  name = name.replace(/(\b)os(\b)/gi, "$1OS$2");
  name = name.replace(/(\b)El Attr(\b)/gi, "");

  if (name.charAt(0) === "$") {
    // This is a name like `$host` for a built-in prop.
    return _.startCase(name.slice(1));
  }

  return name;
};

export const isAccountInHIPAAMode = (accountMeta: AccountMetadata): boolean => {
  return accountMeta.config.hipaaV2 || false;
};

export const isAccountInHIPAAModeV2 = (
  accountMeta: AccountMetadata,
): boolean => {
  return accountMeta.config.hipaaV2 || false;
};

/**
 * @deprecated DO NOT USE. Use the hook `useFeatureFlags` instead.
 */
export const getFeatureFlag = (
  obj: { config?: Record<string, boolean> },
  flagName: string,
): boolean => {
  if (!obj.config) {
    return false;
  }

  return obj.config[flagName] === true;
};

export const getTimezoneString = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const getUTCOffsetMinutes = (): number => {
  // returns the time difference, in minutes, between UTC time and local time.
  // The returned value is positive if the local time zone is behind UTC
  // and negative if the local time zone is ahead of UTC
  return new Date().getTimezoneOffset();
};

export const getTimezoneOffsetString = (): string => {
  let offset = getUTCOffsetMinutes();
  let sign = "-";
  if (offset <= 0) {
    sign = "+";
    offset = -offset;
  }
  const minutes = offset % 60;
  const hours = (offset - minutes) / 60;
  return `UTC${sign}${hours
    .toString()
    .padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
};

// isNetworkFilter - determine if filter is network-specific
//
// The passed filter may be either a db-format or ui-format (with nw_path) filter.
// The check below covers both.
export const isNetworkFilter = (filter: EventDefinitionFilter): boolean => {
  return (
    filter.prop === "$network" || //ui scenario
    filter.prop.startsWith(".") //db scenario
  );
};

export const isNetworkFilterExistsOp = (
  filter: EventDefinitionFilter,
): boolean => {
  return filter.op.endsWith("exists");
};

export const delayAsync = async (timeout: number) => {
  return new Promise((resolve) => setTimeout(resolve, timeout));
};

const tryParseJSON = function (s: string) {
  try {
    return JSON.parse(s).toString();
  } catch {
    return s.toString();
  }
};

// Some of our traces have nested JSON with stringified JSON as a key inside an
// object. Use this to parse those nested maps so they display nicely
export const parseNestedJSON = function (obj: unknown) {
  if (typeof obj === "string") {
    return tryParseJSON(obj);
  } else if (typeof obj === "number" || obj instanceof Number) {
    return obj;
  } else if (obj instanceof Array || obj instanceof Object) {
    return obj;
  } else if (obj === undefined || obj === null) {
    return obj;
  } else {
    for (let [k, v] of Object.entries(obj)) {
      obj[k] = parseNestedJSON(v);
    }
    return obj;
  }
};

export function capitalizeFirstLetter(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// convertCentsToDollars takes a number of cents and converts it
// to a number of dollars
export function convertCentsToDollars(cents: number): number {
  return cents / 100;
}

export function nullableConvertCentsToDollars(
  cents: number | null,
): number | null {
  if (cents === null) {
    return null;
  }
  return convertCentsToDollars(cents);
}

// formatUSDollars takes a number representation of dollars and formats it as USD
//   e.g. 1000 -> $1,000.00
//        1.005 -> $1.01
export function formatUSDollars(value: number): string {
  return value.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

// material-ui/picker is fairly out of date. Because we're using
// it as a date picker library, we need to pass in a date formatter function - normally,
// this would be provided by date-io/date-fns, but due to compatibility
// issues, we need to add an additional shim to support methods that the
// date picker library expects to exist (but don't exist on the date-fns
// library, because it's out of date too).

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "long",
  year: "numeric",
});

const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
  timeZoneName: "short",
});

const hourFormatter = new Intl.DateTimeFormat(undefined, {
  hour12: true,
  hour: "numeric",
});

const minuteFormatter = new Intl.DateTimeFormat(undefined, {
  minute: "numeric",
});

export const calendarHeaderTextFormatter = new Intl.DateTimeFormat(undefined, {
  month: "long",
  year: "numeric",
});

export class CustomDateFnsUtils extends DateFnsUtils {
  getYearText(_date: Date) {
    return "";
  }

  getDatePickerHeaderText(date: Date) {
    return dateFormatter.format(date);
  }

  getDateTimePickerHeaderText(date: Date) {
    return dateTimeFormatter.format(date);
  }

  getDayText(date: Date) {
    return date.getDate();
  }

  getHourText(date: Date) {
    return hourFormatter.format(date).padStart(2, "0");
  }

  getMinuteText(date: Date) {
    return minuteFormatter.format(date).padStart(2, "0");
  }

  getCalendarHeaderText(date: Date) {
    return calendarHeaderTextFormatter.format(date);
  }
}
